import React from 'react';
import {
    ProductsContainerStyled,
    ProductCardStyled,
    ProductModalStyled,
    BadgeStyled,
    PlanNameStyled,
    PriceStyled,
    PriceAmountStyled,
    PricePeriodStyled,
    CardCTAStyled,
    TaglineStyled,
    DividerStyled,
    FeatureListStyled,
    FeatureItemStyled,
    CheckIconStyled,
    CloseButton,
    UseCaseStyled,
    CardFooterStyled
} from '../styles/Products';
import iconMap from '../data/iconMap';
import { ContentHeaderStyled } from '../styles/Main';
import { FaCheck } from 'react-icons/fa';


// All tiers get the same features — the only real differentiator is analyses/month.
// Each "analysis" = 1 run with any number of ZIPs (ZIP mode) or 1 property (manual mode).
const PLAN_META = {
    free: {
        tagline: 'Try the full analysis engine, no card needed',
        features: [
            { text: '5 analyses / month' },
            { text: 'About 1 run per week' },
            { text: 'Multi-ZIP and manual modes' },
            { text: 'AI market data + cash flow' },
            { text: 'Component-level CapEx' },
            { text: 'Resets every 30 days' },
        ],
        useCase: 'Explore a market or two and see the full analysis engine in action — no card needed. If you need more than one run a week, Starter is your next step.',
    },
    Starter: {
        tagline: 'Analyze a few deals per week across markets',
        features: [
            { text: '20 analyses / month' },
            { text: 'About 5 runs per week' },
            { text: 'Multi-ZIP and manual modes' },
            { text: 'AI market data + cash flow' },
            { text: 'Component-level CapEx' },
            { text: 'Resets every billing cycle' },
        ],
        useCase: 'Compare a few markets per week, tweak assumptions, and re-run when new listings drop. At $0.50/analysis, it pays for itself on the first deal you don\'t overpay for.',
    },
    Pro: {
        tagline: 'Run comps daily across multiple markets',
        badge: 'Most Popular',
        features: [
            { text: '40 analyses / month' },
            { text: 'About 1-2 runs per day' },
            { text: 'Multi-ZIP and manual modes' },
            { text: 'AI market data + cash flow' },
            { text: 'Component-level CapEx' },
            { text: 'Resets every billing cycle' },
        ],
        useCase: 'Run daily comps across multiple markets without watching your count. Double Starter\'s volume for just $10 more.',
    },
    Unlimited: {
        tagline: 'No limits — analyze as much as you want',
        features: [
            { text: 'Unlimited analyses' },
            { text: 'No daily or monthly cap' },
            { text: 'Multi-ZIP and manual modes' },
            { text: 'AI market data + cash flow' },
            { text: 'Component-level CapEx' },
            { text: 'Never throttled' },
        ],
        useCase: 'Analyze every listing, test multiple scenarios, never hit a wall. If you\'re researching multiple markets multiple times per day, this is cheaper.',
    },
};

const FeatureList = ({ features }) => (
    <FeatureListStyled>
        {features.map((f, i) => (
            <FeatureItemStyled key={i}>
                <CheckIconStyled>
                    <FaCheck size={12} />
                </CheckIconStyled>
                {f.text}
            </FeatureItemStyled>
        ))}
    </FeatureListStyled>
);


function Products({ plans = [], setShowPlans, onSelectFree, onSelectPlan, loading }) {
    const sortedPlans = [...(plans || [])].sort(
        (a, b) => (a.prices?.priceData?.unit_amount || 0) - (b.prices?.priceData?.unit_amount || 0)
    );

    const freeCard = () => {
        const meta = PLAN_META.free;
        return (
            <ProductCardStyled>
                <PlanNameStyled>Free</PlanNameStyled>
                <PriceStyled>
                    <PriceAmountStyled>$0</PriceAmountStyled>
                    <PricePeriodStyled>/ mo</PricePeriodStyled>
                </PriceStyled>
                <CardCTAStyled onClick={onSelectFree}>
                    Try Free
                </CardCTAStyled>
                <TaglineStyled>{meta.tagline}</TaglineStyled>
                <DividerStyled />
                <FeatureList features={meta.features} />
                <CardFooterStyled>
                    <DividerStyled />
                    <UseCaseStyled>{meta.useCase}</UseCaseStyled>
                </CardFooterStyled>
            </ProductCardStyled>
        );
    };

    const paidCard = (plan) => {
        const meta = PLAN_META[plan.name] || {};
        const price = (plan.prices.priceData.unit_amount / 100).toFixed(2);
        const isHighlighted = !!meta.badge;

        return (
            <ProductCardStyled key={plan.productId} $highlighted={isHighlighted}>
                {meta.badge && <BadgeStyled>{meta.badge}</BadgeStyled>}
                <PlanNameStyled>{plan.name}</PlanNameStyled>
                <PriceStyled>
                    <PriceAmountStyled>${price}</PriceAmountStyled>
                    <PricePeriodStyled>/ mo</PricePeriodStyled>
                </PriceStyled>
                <CardCTAStyled $primary={isHighlighted} onClick={() => onSelectPlan(plan.prices.priceId)}>
                    {isHighlighted ? 'Get Started' : 'Buy It'}
                </CardCTAStyled>
                <TaglineStyled>{meta.tagline || plan.description}</TaglineStyled>
                <DividerStyled />
                <FeatureList features={meta.features || []} />
                <CardFooterStyled>
                    <DividerStyled />
                    {meta.useCase && <UseCaseStyled>{meta.useCase}</UseCaseStyled>}
                </CardFooterStyled>
            </ProductCardStyled>
        );
    };

    return (
        <ProductModalStyled>
            {setShowPlans && (
                <CloseButton onClick={() => setShowPlans(false)}>
                    {iconMap['X']}
                </CloseButton>
            )}
            <ContentHeaderStyled>Choose Your Plan</ContentHeaderStyled>
            {loading ? (
                <ProductsContainerStyled>
                    {freeCard()}
                    {sortedPlans.map((p) => paidCard(p))}
                </ProductsContainerStyled>
            ) : (
                <div>Loading plans...</div>
            )}
        </ProductModalStyled>
    );
}

export default Products;
